/**
 * Типы данных для измеряемых атрибутов событий посадок
 */

export type PropertyDataType = 'integer' | 'text' | 'float' | 'boolean';

export type PropertyObjectType = 'planting' | 'bed' | 'plot' | 'field';

export type SemanticType = 'measured' | 'input' | 'fact' | 'derived' | 'structural' | 'resource';

/**
 * Свойство (измеряемый параметр)
 */
export interface Property {
  id: number;
  code: string;
  name: string;
  objectType: PropertyObjectType;
  dataType: PropertyDataType;
  dataTypeCode: number; // 0=integer, 1=text, 2=float, 3=boolean
  unitId: number | null;
  unit?: string; // Текстовое представление единицы измерения (из шаблона)
  options: string[]; // Для типа 'text' - список доступных значений
  isService: boolean;
  semanticType: SemanticType;
}

/**
 * Значение свойства посадки (текущее состояние)
 */
export interface PlantingAttributeValue {
  plantingId: string;
  propertyId: number;
  valueNumber: number | null;
  valueText: string | null;
  valueBoolean: boolean | null;
  updatedAt: string;
  propertyCode: string;
  propertyName: string;
  dataType: number;
  unit: string | null;
}

/**
 * Payload для создания события посадки
 */
export interface PlantingEventPayload {
  kind?: string | null; // Опционально: "measurement" или "service". Если не указано, backend определяет автоматически по свойствам
  notes?: string | null; // Примечания
  assignedTo?: string | null; // UUID пользователя для назначения задачи
  simSessionId?: string | null; // UUID сессии симуляции (если событие должно быть временным)
  eventDate?: string | null; // ISO date для ретро/future событий
  isLateObservation?: boolean; // Для real-режима: явное подтверждение позднего факта
  auditOverrideBy?: string | null; // UUID суперпользователя для override immutable policy
  sourceType?: EventSourceType;
  sourceRef?: string | null;
  sourceConfidence?: number | null;
  decisionState?: EventDecisionState;
  properties?: {
    quantity_affected?: number; // Для batch plantings: количество затронутых единиц
    quantityAffected?: number; // camelCase alias for device/frontend clients
    [propertyId: string]: number | string | boolean | undefined;
  };
}

/**
 * Ответ при создании события
 */
export interface PlantingEventResponse {
  success: boolean;
  event: {
    id: string;
    kind: string | null;
    measuredPropertiesUpdated: number;
  };
  transplant: any | null;
}

/**
 * Привязка свойства к культуре
 */
export interface CropPropertyBinding {
  id: number;
  cropId: number;
  propertyId: number;
  isRequired: boolean;
  includeInPricelist: boolean;
  sortOrder: number;
  createdAt: string;
  property: {
    code: string;
    name: string;
    dataType: number; // 0=integer, 1=text, 2=float, 3=boolean
    semanticType: SemanticType;
    unit: string | null;
    options: Array<{
      id: number;
      value: string;
      code: string;
      sortOrder: number;
    }>;
  };
}

/**
 * Payload для создания привязки свойства к культуре
 */
export interface CreateCropPropertyBindingPayload {
  propertyId: number;
  isRequired?: boolean;
  includeInPricelist?: boolean;
  sortOrder?: number;
}

/**
 * Payload для обновления привязки свойства к культуре
 */
export interface UpdateCropPropertyBindingPayload {
  isRequired?: boolean;
  includeInPricelist?: boolean;
  sortOrder?: number;
}

/**
 * Адаптер для Property
 */
export function adaptProperty(data: any): Property {
  // Преобразуем options: если это массив объектов {id, value, code, sortOrder},
  // берем поле value или title, иначе оставляем как есть
  let options: string[] = [];
  if (Array.isArray(data.options)) {
    options = data.options.map((opt: any) => {
      if (typeof opt === 'string') {
        return opt;
      }
      // Если это объект, берем value, title или code
      return opt.value ?? opt.title ?? opt.code ?? String(opt.id ?? opt);
    });
  }

  return {
    id: Number(data.id),
    code: data.code,
    name: data.name,
    objectType: data.objectType || data.object_type,
    dataType: data.dataType || data.data_type,
    dataTypeCode: data.dataTypeCode ?? data.data_type_code ?? 0,
    unitId: data.unitId ?? data.unit_id ?? null,
    options,
    isService: data.isService ?? data.is_service ?? false,
    semanticType: data.semanticType ?? data.semantic_type,
  };
}

/**
 * Статус события в очереди
 */
export type EventQueueStatus = 'planned' | 'pending' | 'in_progress' | 'completed' | 'cancelled';

/**
 * Тип события
 */
export type EventQueueType = 
  | 'inspection'
  | 'measurement'
  | 'pruning'
  | 'watering'
  | 'fertilizing'
  | 'pest_control'
  | 'weeding'
  | 'general';

/**
 * Тип события (CN-0008)
 */
export type EventKind = 'measurement' | 'service';
export type EventSourceType = 'user' | 'worker' | 'device' | 'external_api' | 'simulation';
export type EventDecisionState = 'proposed' | 'confirmed' | 'rejected';

/**
 * Событие в очереди
 */
export interface EventQueueItem {
  id: string;
  storeId?: string;
  type: string;
  kind?: EventKind | null; // CN-0008: measurement (измерение) или service (уход)
  status: EventQueueStatus;
  plantingId: string; // UUID
  plantingType: 'single' | 'batch';
  plantingQuantity: number;
  quantityAffected?: number | null; // Для batch plantings: сколько единиц затронуто (CN-0007)
  cropId: number;
  cropName: string;
  cropLatinName: string;
  plannedDate: string; // ISO date
  actualDate: string | null;
  notes: string | null;
  // Location names (CN-0007)
  fieldName?: string | null;
  plotName?: string | null;
  bedName?: string | null; // "Ряд X, Позиция Y"
  // CN-0008: Event Templates
  templateId?: number | null;
  templateName?: string | null;
  templateCode?: string | null;
  templateCategory?: string | null;
  templateIcon?: string | null;
  templateColor?: string | null;
  // CN-0008: Assignment data (updated structure from backend)
  assignedTo?: {
    id: string; // user UUID
    name: string; // username for display (fallback: email if username not available)
  } | null;
  assignedAt?: string | null; // ISO datetime
  assignedBy?: {
    id: string; // user UUID
    name: string; // username (fallback: email if username not available)
  } | null;
  sourceType?: EventSourceType;
  sourceRef?: string | null;
  sourceConfidence?: number | null;
  decisionState?: EventDecisionState;
  decisionNote?: string | null;
  decisionBy?: string | null;
  decisionAt?: string | null;
  // Атрибуты события (полная информация о задании)
  properties?: PlantingEventProperty[];
  // Batch Events (групповые задания)
  isBatch?: boolean; // true если это родительское событие
  batchTotal?: number; // Общее количество дочерних событий
  batchCompleted?: number; // Количество завершенных дочерних событий
  parentEventId?: string | null; // ID родительского события (для child events)
  sequenceNumber?: number | null; // Порядковый номер в группе (для child events)
}

/**
 * Ответ для очереди событий
 */
export interface EventQueueResponse {
  total: number;
  completed: number;
  pending: number;
  progress: number; // 0-100
  byStatus?: {
    in_progress: number;
    planned: number;
    completed: number;
  };
  events: EventQueueItem[];
}

/**
 * Дочернее событие в batch (для детализации)
 */
export interface BatchChildEvent {
  id: string;
  parentEventId: string | null;
  batchSequence: number; // Позиция в batch (1-based)
  batchTotal: number; // Общее количество детей в batch
  plantingId: number | null;
  bedId: number | null;
  plotId: number | null;
  fieldId: number | null;
  cropId: number | null; // ID культуры (для посадок)
  templateId: number;
  templateName?: string;
  type: string;
  status: EventQueueStatus;
  cropName?: string;
  cropLatinName?: string;
  plannedDate?: string;
  actualDate?: string | null;
  notes?: string | null;
  properties?: PlantingEventProperty[];
  // Location names (CN-0007)
  fieldName?: string | null;
  plotName?: string | null;
  bedName?: string | null; // "Ряд X, Позиция Y"
}

/**
 * Родительское событие batch
 */
export interface BatchParentEvent {
  id: string;
  templateId: number;
  type: string;
  status: EventQueueStatus;
  batchTotal: number;
  childrenCount: number;
  completedCount: number;
  pendingCount: number;
  progress: number; // 0-100
  properties?: Array<{
    propertyId: number;
    name: string;
    dataType: 'number' | 'text' | 'boolean' | 'date';
    unit?: string;
    isRequired: boolean;
  }>;
}

/**
 * Ответ для списка дочерних событий
 */
export interface BatchChildrenResponse {
  parent: BatchParentEvent;
  children: BatchChildEvent[];
}

/**
 * Payload для завершения события
 */
export interface CompleteEventPayload {
  properties?: {
    [propertyId: string]: number | string | boolean;
  };
  notes?: string;
  quantity_affected?: number; // Для групповых посадок (CN-0007)
}

/**
 * Ответ при завершении события
 */
export interface CompleteEventResponse {
  event: {
    id: string;
    status: 'completed' | 'planned';
    actualDate: string | null; // ISO datetime or null if still planned
    parentEventId: string | null;
    plantingId: string | null; // UUID посадки для инвалидации кэша
  };
  updatedProperties: number;
  partialCompletion?: {
    quantityAffected: number;
    totalQuantity: number;
    isFullyCompleted: boolean;
  };
}

/**
 * Атрибут события в истории
 */
export interface PlantingEventProperty {
  propertyId: number;
  propertyCode: string;
  propertyName: string;
  isService?: boolean;
  semanticType: SemanticType;
  valueText: string | null;
  valueNumber: number | null;
  valueBoolean: boolean | null;
  unit?: string | null;
}

/**
 * Событие в истории посадки
 */
export interface PlantingEvent {
  id: string;
  type: string;
  status: string;
  kind: string | null;
  createdAt?: string | null;
  plannedDate: string | null;
  actualDate: string | null;
  notes: string | null;
  reportedBy: string;
  updatedBy: string;
  quantityAffected: number | null;
  plantingType?: string | null;
  plantingQuantity?: number | null;
  templateName?: string | null;
  templateCode?: string | null;
  properties: PlantingEventProperty[];
  // Location names (CN-0007)
  fieldName?: string | null;
  plotName?: string | null;
  bedName?: string | null; // "Ряд X, Позиция Y"
  // CN-0008: Event Templates
  templateId?: number | null;
  // CN-0008: Assignment data (updated structure from backend)
  assignedTo?: {
    id: string; // user UUID
    name: string; // username for display (fallback: email if username not available)
  } | null;
  assignedAt?: string | null;
  assignedBy?: {
    id: string; // user UUID
    name: string; // username (fallback: email if username not available)
  } | null;
  sourceType?: EventSourceType;
  sourceRef?: string | null;
  sourceConfidence?: number | null;
  decisionState?: EventDecisionState;
  decisionNote?: string | null;
  decisionBy?: string | null;
  decisionAt?: string | null;
}

export interface EventDecisionPayload {
  note?: string;
}

export interface EventDecisionResponse {
  id: string;
  decisionState: EventDecisionState;
  decisionNote?: string | null;
  decisionBy?: string | null;
  decisionAt?: string | null;
  sourceType?: EventSourceType;
  sourceRef?: string | null;
  sourceConfidence?: number | null;
}

/**
 * Ответ истории событий посадки
 */
export interface PlantingEventsHistoryResponse {
  plantingId: string;
  plantingType: 'single' | 'batch';
  totalEvents: number;
  events: PlantingEvent[];
}

/**
 * Адаптер для PlantingAttributeValue
 */
export function adaptPlantingAttributeValue(data: any): PlantingAttributeValue {
  return {
    plantingId: data.plantingId ?? data.planting_id,
    propertyId: data.propertyId ?? data.property_id,
    valueNumber: data.valueNumber ?? data.value_number ?? null,
    valueText: data.valueText ?? data.value_text ?? null,
    valueBoolean: data.valueBoolean ?? data.value_boolean ?? null,
    updatedAt: data.updatedAt ?? data.updated_at,
    propertyCode: data.propertyCode ?? data.property_code,
    propertyName: data.propertyName ?? data.property_name,
    dataType: data.dataType ?? data.data_type,
    unit: data.unit ?? null,
  };
}
